/**
 * portal/BookPage.js — book a retreat, booking.com / Airbnb style.
 *
 *   Dates → Pathway → Room → Activity → Diet → Check & confirm
 *
 * Steps that don't apply are skipped automatically (a retreat with no
 * pathways goes straight to rooms; an invitation for one specific retreat
 * skips the date step). A live price summary follows you on the right
 * (bottom bar on mobile). The last step uses the GOV.UK "check answers"
 * pattern — every choice listed with a Change link.
 *
 * Prices here are a preview; the server recalculates and snapshots the
 * real total when the booking is confirmed (services/booking.service.js).
 * No payment is taken in this flow.
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  HiOutlineCalendarDays, HiOutlineMapPin, HiOutlineCheck, HiOutlineCheckCircle, HiOutlineArrowRight,
  HiOutlineArrowLeft, HiOutlineSparkles, HiOutlineHome, HiOutlineSun, HiOutlineHeart, HiOutlineClipboardDocumentCheck,
  HiOutlineUsers, HiOutlineClock, HiOutlinePhoto, HiOutlinePencilSquare, HiOutlineLockClosed,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./BookPage.css";
import Picture from "../components/utils/Picture";

const ease = [0.22, 1, 0.36, 1];

// Dates come from MySQL DATE columns serialised as UTC midnight — format in
// UTC so nobody west of Greenwich sees the day before.
const fmt = (d, opts = { month: "short", day: "numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-US", { ...opts, timeZone: "UTC" }) : "";
const fmtLong = (d) => fmt(d, { month: "long", day: "numeric", year: "numeric" });
const range = (a, b) => `${fmt(a)} – ${fmt(b, { month: "short", day: "numeric", year: "numeric" })}`;
const money = (n) => (Number(n) > 0 ? `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "$0");

const STEP_META = {
  dates:    { label: "Dates",    Icon: HiOutlineCalendarDays },
  pathway:  { label: "Pathway",  Icon: HiOutlineSparkles },
  room:     { label: "Room",     Icon: HiOutlineHome },
  activity: { label: "Activity", Icon: HiOutlineSun },
  diet:     { label: "Diet",     Icon: HiOutlineHeart },
  review:   { label: "Confirm",  Icon: HiOutlineClipboardDocumentCheck },
};

/** Mirrors booking.service.quote() so the preview matches what the server charges. */
function quote(block, pathway, room) {
  if (!block) return { nights: 0, pathwayPrice: 0, nightly: 0, roomTotal: 0, total: 0 };
  if (pathway) {
    const nightly = room && !room.is_free ? Number(room.nightly_rate || 0) : 0;
    const roomTotal = nightly * pathway.nights;
    return { nights: pathway.nights, pathwayPrice: Number(pathway.price), nightly, roomTotal, total: Number(pathway.price) + roomTotal };
  }
  const roomTotal = room && !room.is_free ? Number(room.rate || 0) : 0;
  return { nights: block.nights, pathwayPrice: 0, nightly: 0, roomTotal, total: roomTotal };
}

function Spots({ remaining, capacity }) {
  if (remaining <= 0) return <span className="bk__spots bk__spots--full">Full</span>;
  const low = remaining <= 3;
  return (
    <span className={`bk__spots ${low ? "bk__spots--low" : ""}`}>
      <HiOutlineUsers /> {low ? `Only ${remaining} left` : `${remaining} of ${capacity} places left`}
    </span>
  );
}

function RoomPhotos({ photos, name }) {
  const [i, setI] = useState(0);
  if (!photos || photos.length === 0) {
    return <div className="bk__room-photo bk__room-photo--empty"><HiOutlinePhoto /></div>;
  }
  return (
    <div className="bk__room-photo">
      <Picture src={photos[i].file_path} alt={name} sizes="(min-width: 900px) 360px, 100vw" width={480} height={320} />
      {photos.length > 1 && (
        <div className="bk__room-dots" onClick={(e) => e.stopPropagation()}>
          {photos.slice(0, 6).map((p, n) => (
            <button key={p.id} type="button" aria-label={`Photo ${n + 1}`} className={n === i ? "is-on" : ""} onClick={() => setI(n)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Summary card (sticky right / bottom bar on mobile) ── */
function Summary({ block, pathway, room, activity, diet, q, onContinue, continueLabel, canContinue, busy }) {
  return (
    <aside className="bk__summary">
      <div className="pc bk__summary-card">
        <p className="bk__summary-eyebrow">Your booking</p>
        {!block ? (
          <p className="bk__summary-empty">Choose your retreat dates to start.</p>
        ) : (
          <>
            <h3 className="bk__summary-title">{block.name}</h3>
            <p className="bk__summary-meta"><HiOutlineCalendarDays /> {pathway ? range(pathway.start_date, pathway.end_date) : range(block.start_date, block.end_date)}</p>
            {block.location && <p className="bk__summary-meta"><HiOutlineMapPin /> {block.location}</p>}
            {(pathway || room || activity || diet) && <dl className="bk__lines">
              {pathway && (<><dt>{pathway.name}</dt><dd>{money(q.pathwayPrice)}</dd></>)}
              {room && (
                <>
                  <dt>{room.name}{pathway && q.nightly > 0 ? <small> {money(q.nightly)} × {q.nights} nights</small> : null}</dt>
                  <dd>{q.roomTotal > 0 ? money(q.roomTotal) : "Included"}</dd>
                </>
              )}
              {activity && (<><dt>{activity.name}</dt><dd>Included</dd></>)}
              {diet && (<><dt>Diet: {diet.name}</dt><dd /></>)}
            </dl>}
            <div className="bk__total"><span>Total</span><strong>{money(q.total)}</strong></div>
            <p className="bk__summary-note">No payment is taken today. Our team will be in touch about payment options.</p>
          </>
        )}
        {onContinue && (
          <button type="button" className="bk__continue" disabled={!canContinue || busy} onClick={onContinue}>
            {busy ? "Confirming…" : continueLabel} {!busy && <HiOutlineArrowRight />}
          </button>
        )}
      </div>
    </aside>
  );
}

/* ── Already booked: manage activity + diet ── */
function ManageBooking({ booking, activities, diets, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [activityId, setActivityId] = useState(booking.block_activity_id || "");
  const [dietId, setDietId] = useState(booking.diet_option_id || "");
  const [notes, setNotes] = useState(booking.diet_notes || "");
  const [saving, setSaving] = useState(false);
  const started = new Date(booking.block_start_date) <= new Date();

  const save = async () => {
    setSaving(true);
    try {
      const res = await portalAPI.booking.update({ block_activity_id: activityId || null, diet_option_id: dietId || null, diet_notes: notes });
      toast.success(res?.message || "Booking updated.");
      setEditing(false);
      onSaved(res.data);
    } catch (err) {
      toast.error(err.message || "Could not update your booking.");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    ["Retreat", booking.block_name, `${fmtLong(booking.block_start_date)} – ${fmtLong(booking.block_end_date)}`],
    booking.pathway_name && ["Pathway", booking.pathway_name, `${fmtLong(booking.pathway_start_date)} – ${fmtLong(booking.pathway_end_date)} · ${booking.nights} nights`],
    booking.room_type_name && ["Room", booking.room_type_name, Number(booking.room_total) > 0 ? `${money(booking.room_total)}` : "Included"],
    ["Activity", booking.activity_name || "Not chosen yet", null],
    ["Diet", booking.diet_name || "Not specified", booking.diet_notes || null],
  ].filter(Boolean);

  return (
    <div className="bk__manage">
      <div className="pc bk__confirmed">
        <div className="bk__confirmed-icon"><HiOutlineCheckCircle /></div>
        <div>
          <p className="bk__confirmed-eyebrow">You're booked</p>
          <h2 className="bk__confirmed-title">{booking.block_name}</h2>
          <p className="bk__confirmed-sub">{booking.block_location ? `${booking.block_location} · ` : ""}{fmtLong(booking.pathway_start_date || booking.block_start_date)}</p>
        </div>
        <div className="bk__confirmed-total"><span>Total</span><strong>{money(booking.total)}</strong></div>
      </div>

      <div className="pc">
        <div className="bk__check-head">
          <p className="p-section-title">Your booking details</p>
          {!started && !editing && (activities.length > 0 || diets.length > 0) && (
            <button type="button" className="p-btn p-btn--outline" onClick={() => setEditing(true)}>
              <HiOutlinePencilSquare /> Change activity or diet
            </button>
          )}
        </div>

        {!editing ? (
          <dl className="bk__check">
            {rows.map(([k, v, sub]) => (
              <div key={k} className="bk__check-row">
                <dt>{k}</dt>
                <dd><span>{v}</span>{sub && <small>{sub}</small>}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="bk__edit">
            {activities.length > 0 && (
              <fieldset className="bk__fieldset">
                <legend>Included activity</legend>
                <div className="bk__pill-grid">
                  {activities.map((a) => {
                    const mine = Number(a.id) === Number(booking.block_activity_id);
                    const full = a.remaining <= 0 && !mine;
                    return (
                      <label key={a.id} className={`bk__pill ${Number(activityId) === Number(a.id) ? "is-on" : ""} ${full ? "is-off" : ""}`}>
                        <input type="radio" name="edit-activity" disabled={full} checked={Number(activityId) === Number(a.id)} onChange={() => setActivityId(a.id)} />
                        {a.name} {full ? <em>Full</em> : <em>{a.remaining} left</em>}
                      </label>
                    );
                  })}
                  <label className={`bk__pill ${!activityId ? "is-on" : ""}`}>
                    <input type="radio" name="edit-activity" checked={!activityId} onChange={() => setActivityId("")} /> Decide later
                  </label>
                </div>
              </fieldset>
            )}
            {diets.length > 0 && (
              <fieldset className="bk__fieldset">
                <legend>Diet</legend>
                <div className="bk__pill-grid">
                  {diets.map((d) => (
                    <label key={d.id} className={`bk__pill ${Number(dietId) === Number(d.id) ? "is-on" : ""}`}>
                      <input type="radio" name="edit-diet" checked={Number(dietId) === Number(d.id)} onChange={() => setDietId(d.id)} /> {d.name}
                    </label>
                  ))}
                </div>
                <label className="bk__label" htmlFor="edit-notes">Allergies or anything the kitchen should know</label>
                <textarea id="edit-notes" className="bk__textarea" rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </fieldset>
            )}
            <div className="bk__edit-actions">
              <button type="button" className="p-btn p-btn--outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button type="button" className="p-btn p-btn--rust" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        )}
        <p className="bk__fine">Need to change your dates, pathway or room? Reply to any message from our team and we'll sort it out.</p>
      </div>

      <div className="bk__next-links">
        <Link to="/portal/my-retreat" className="bk__next-link"><HiOutlineUsers /> Meet your group &amp; see your room</Link>
        <Link to="/portal/resources" className="bk__next-link"><HiOutlineClipboardDocumentCheck /> Read the ‘Before you go’ guides</Link>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════ */
export default function BookPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState("dates");
  const [sel, setSel] = useState({ blockId: null, pathwayId: null, roomId: null, activityId: null, dietId: null, dietNotes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    portalAPI.booking.options()
      .then((res) => {
        const d = res?.data || null;
        setData(d);
        if (d?.eligible && d.blocks.length > 0) {
          const only = d.targetedBlockId ? d.blocks.find((b) => b.id === d.targetedBlockId) : d.blocks.length === 1 ? d.blocks[0] : null;
          if (only) {
            setSel((s) => ({ ...s, blockId: only.id, roomId: d.reservedRoomTypeId || null }));
            setStep(only.has_pathways ? "pathway" : only.room_types.length ? "room" : only.activities.length ? "activity" : d.diets.length ? "diet" : "review");
          }
        }
      })
      .catch((err) => setError(err.message || "Could not load booking options."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const block = data?.blocks?.find((b) => b.id === sel.blockId) || null;
  const pathway = block?.pathways.find((p) => p.id === sel.pathwayId) || null;
  const room = block?.room_types.find((r) => r.id === sel.roomId) || null;
  const activity = block?.activities.find((a) => a.id === sel.activityId) || null;
  const diet = data?.diets?.find((d) => d.id === sel.dietId) || null;
  const q = quote(block, pathway, room);

  const steps = useMemo(() => {
    // "Dates" always shows for context — it's pre-filled and locked when
    // there's only one retreat to choose from.
    const list = ["dates"];
    // Before a retreat is picked, show every step any retreat could need so
    // the stepper doesn't change shape under the member's feet.
    const pool = block ? [block] : data?.blocks || [];
    if (pool.some((b) => b.has_pathways)) list.push("pathway");
    if (pool.some((b) => b.room_types.length > 0)) list.push("room");
    if (pool.some((b) => b.activities?.length > 0)) list.push("activity");
    if (data?.diets?.length) list.push("diet");
    list.push("review");
    return list;
  }, [data, block]);

  const done = {
    dates: !!block,
    pathway: !!pathway,
    room: !!room,
    activity: true, // optional
    diet: !!diet || !(data?.diets?.length),
    review: false,
  };
  const idx = steps.indexOf(step);
  const canContinue = step === "review" ? steps.slice(0, -1).every((s) => done[s]) : done[step];
  const lockedDates = !!data?.targetedBlockId || (data?.blocks?.length || 0) === 1;

  const go = (s) => { setStep(s); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = () => {
    if (step === "review") return confirm();
    go(steps[Math.min(idx + 1, steps.length - 1)]);
  };
  const back = () => go(steps[Math.max(idx - 1, 0)]);

  const pickBlock = (b) => {
    setSel((s) => ({ ...s, blockId: b.id, pathwayId: null, roomId: data.reservedRoomTypeId || null, activityId: null }));
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await portalAPI.booking.create({
        retreat_block_id: sel.blockId,
        pathway_id: sel.pathwayId,
        room_type_id: sel.roomId,
        block_activity_id: sel.activityId,
        diet_option_id: sel.dietId,
        diet_notes: sel.dietNotes,
      });
      toast.success(res?.message || "You're booked!");
      window.scrollTo({ top: 0, behavior: "smooth" });
      load();
    } catch (err) {
      toast.error(err.message || "Something changed — please check your choices.");
      load(); // availability may have changed; refresh the numbers
    } finally {
      setBusy(false);
    }
  };

  /* ── States ── */
  let content;
  if (loading && !data) {
    content = <div className="bk__loading"><div className="bk__sk" /><div className="bk__sk" /><div className="bk__sk" /></div>;
  } else if (error) {
    content = <div className="pc bk__state"><p>{error}</p><button type="button" className="p-btn p-btn--outline" onClick={load}>Try again</button></div>;
  } else if (data?.booking) {
    content = <ManageBooking booking={data.booking} activities={data.activities || []} diets={data.diets || []} onSaved={() => load()} />;
  } else if (!data?.eligible) {
    const msg = {
      no_window: ["Booking isn't open for you yet", "We release booking windows in small groups so everyone gets a fair chance. We'll email you the moment yours opens."],
      expired: ["Your booking window has closed", "No problem — reply to any message from our team and we'll open a new window for you."],
    }[data?.reason] || ["Booking isn't available right now", "Please check back soon."];
    content = (
      <div className="pc bk__state">
        <div className="bk__state-icon"><HiOutlineLockClosed /></div>
        <h2>{msg[0]}</h2>
        <p>{msg[1]}</p>
        <Link to="/portal/dashboard" className="p-btn p-btn--outline">See your next steps</Link>
      </div>
    );
  } else if (data.blocks.length === 0) {
    content = (
      <div className="pc bk__state">
        <div className="bk__state-icon"><HiOutlineCalendarDays /></div>
        <h2>All open retreats are full right now</h2>
        <p>New dates open regularly. Your booking window stays open{data.expiresAt ? ` until ${fmtLong(data.expiresAt)}` : ""} — check back soon, or reply to our team and we'll help.</p>
      </div>
    );
  } else {
    content = (
      <>
        {/* Stepper */}
        <ol className="bk__stepper" aria-label="Booking steps">
          {steps.map((s, i) => {
            const M = STEP_META[s];
            const reachable = i <= idx || steps.slice(0, i).every((x) => done[x]);
            const isDone = i < idx && done[s];
            return (
              <li key={s} className={`bk__step ${s === step ? "is-current" : ""} ${isDone ? "is-done" : ""}`}>
                <button type="button" disabled={!reachable || (s === "dates" && lockedDates && i !== idx)} onClick={() => go(s)}>
                  <span className="bk__step-dot">{isDone ? <HiOutlineCheck /> : <M.Icon />}</span>
                  <span className="bk__step-label">{M.label}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {data.expiresAt && (
          <p className="bk__deadline"><HiOutlineClock /><span>Your booking window is open until <strong>{fmtLong(data.expiresAt)}</strong>. Places are confirmed first come, first served.</span></p>
        )}

        <div className="bk__layout">
          <div className="bk__main">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.28, ease }}>

                {step === "dates" && (
                  <section>
                    <h2 className="bk__h2">When would you like to come?</h2>
                    <p className="bk__lead">{lockedDates ? "Your invitation is for this retreat." : "Choose the retreat that suits you. You'll pick your pathway and room next."}</p>
                    <div className="bk__cards">
                      {data.blocks.map((b) => (
                        <button key={b.id} type="button" disabled={!b.bookable || (lockedDates && b.id !== sel.blockId)} className={`bk__card bk__card--date ${sel.blockId === b.id ? "is-on" : ""}`} onClick={() => pickBlock(b)}>
                          <span className="bk__date-badge">
                            <span className="bk__date-mon">{fmt(b.start_date, { month: "short" })}</span>
                            <span className="bk__date-day">{fmt(b.start_date, { day: "numeric" })}</span>
                          </span>
                          <span className="bk__card-body">
                            <span className="bk__card-title">{b.name}</span>
                            <span className="bk__card-meta"><HiOutlineCalendarDays /> {range(b.start_date, b.end_date)} · {b.nights} nights</span>
                            {b.location && <span className="bk__card-meta"><HiOutlineMapPin /> {b.location}</span>}
                            {b.has_pathways && <span className="bk__card-meta"><HiOutlineSparkles /> {b.pathways.length} pathway{b.pathways.length !== 1 ? "s" : ""} to choose from</span>}
                          </span>
                          <span className="bk__card-side">
                            {b.bookable ? (Number(b.from_price) > 0 ? <><small>from</small><strong>{money(b.from_price)}</strong></> : <><small>places from</small><strong>Free</strong></>) : <span className="bk__spots bk__spots--full">Full</span>}
                          </span>
                          {sel.blockId === b.id && <span className="bk__tick"><HiOutlineCheck /></span>}
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {step === "pathway" && block && (
                  <section>
                    <h2 className="bk__h2">Choose your pathway</h2>
                    <p className="bk__lead">Each pathway has its own dates, programme and number of places.</p>
                    <div className="bk__cards">
                      {block.pathways.map((p) => (
                        <button key={p.id} type="button" disabled={p.remaining <= 0} className={`bk__card bk__card--pathway ${sel.pathwayId === p.id ? "is-on" : ""}`} onClick={() => setSel((s) => ({ ...s, pathwayId: p.id }))}>
                          <span className="bk__card-body">
                            <span className="bk__card-title">{p.name}</span>
                            <span className="bk__card-meta"><HiOutlineCalendarDays /> {range(p.start_date, p.end_date)} · {p.nights} nights</span>
                            {p.description && <span className="bk__card-desc">{p.description}</span>}
                            {p.highlights?.length > 0 && (
                              <span className="bk__highlights">
                                {p.highlights.map((h) => <span key={h}><HiOutlineCheck /> {h}</span>)}
                              </span>
                            )}
                            <Spots remaining={p.remaining} capacity={p.capacity} />
                          </span>
                          <span className="bk__card-side"><strong>{money(p.price)}</strong><small>per person</small></span>
                          {sel.pathwayId === p.id && <span className="bk__tick"><HiOutlineCheck /></span>}
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {step === "room" && block && (
                  <section>
                    <h2 className="bk__h2">Choose your room</h2>
                    <p className="bk__lead">
                      {pathway ? `Room upgrades are priced per night — ${pathway.nights} nights on ${pathway.name}.` : "Where you'll stay for the retreat."}
                    </p>
                    <div className="bk__rooms">
                      {block.room_types.map((r) => {
                        const reserved = data.reservedRoomTypeId && Number(data.reservedRoomTypeId) === Number(r.id);
                        const blocked = data.reservedRoomTypeId && !reserved;
                        const nightly = r.is_free ? 0 : Number(r.nightly_rate || 0);
                        const total = pathway ? nightly * pathway.nights : r.is_free ? 0 : Number(r.rate || 0);
                        const full = r.remaining <= 0 && !reserved;
                        return (
                          <button key={r.id} type="button" disabled={full || blocked} className={`bk__room ${sel.roomId === r.id ? "is-on" : ""}`} onClick={() => setSel((s) => ({ ...s, roomId: r.id }))}>
                            <RoomPhotos photos={r.photos} name={r.name} />
                            <span className="bk__room-body">
                              <span className="bk__card-title">{r.name}{reserved && <em className="bk__reserved">Reserved for you</em>}</span>
                              {r.description && <span className="bk__card-desc">{r.description}</span>}
                              <span className="bk__room-price">
                                {pathway
                                  ? nightly > 0 ? <><strong>+{money(nightly)}</strong> / night <small>= {money(total)}</small></> : <strong>Included</strong>
                                  : total > 0 ? <strong>{money(total)}</strong> : <strong>Included</strong>}
                              </span>
                              {full ? <span className="bk__spots bk__spots--full">Full</span> : !reserved && <Spots remaining={r.remaining} capacity={r.capacity} />}
                            </span>
                            {sel.roomId === r.id && <span className="bk__tick"><HiOutlineCheck /></span>}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                {step === "activity" && block && (
                  <section>
                    <h2 className="bk__h2">Pick your included activity</h2>
                    <p className="bk__lead">One activity is included with your retreat. Places are limited.</p>
                    <div className="bk__cards bk__cards--grid">
                      {block.activities.map((a) => (
                        <button key={a.id} type="button" disabled={a.remaining <= 0} className={`bk__card bk__card--tile ${sel.activityId === a.id ? "is-on" : ""}`} onClick={() => setSel((s) => ({ ...s, activityId: a.id }))}>
                          <span className="bk__tile-icon"><HiOutlineSun /></span>
                          <span className="bk__card-title">{a.name}</span>
                          {a.description && <span className="bk__card-desc">{a.description}</span>}
                          {a.activity_date && <span className="bk__card-meta"><HiOutlineCalendarDays /> {fmtLong(a.activity_date)}</span>}
                          <Spots remaining={a.remaining} capacity={a.capacity} />
                          {sel.activityId === a.id && <span className="bk__tick"><HiOutlineCheck /></span>}
                        </button>
                      ))}
                      <button type="button" className={`bk__card bk__card--tile bk__card--later ${sel.activityId === null ? "is-soft" : ""}`} onClick={() => setSel((s) => ({ ...s, activityId: null }))}>
                        <span className="bk__tile-icon"><HiOutlineClock /></span>
                        <span className="bk__card-title">Decide later</span>
                        <span className="bk__card-desc">You can choose from your booking page any time before the retreat, while places last.</span>
                      </button>
                    </div>
                  </section>
                )}

                {step === "diet" && (
                  <section>
                    <h2 className="bk__h2">Your diet</h2>
                    <p className="bk__lead">All meals are prepared on site. Tell us how you eat so the kitchen can plan.</p>
                    <div className="bk__pill-grid bk__pill-grid--lg" role="radiogroup">
                      {data.diets.map((d) => (
                        <label key={d.id} className={`bk__pill ${sel.dietId === d.id ? "is-on" : ""}`}>
                          <input type="radio" name="diet" checked={sel.dietId === d.id} onChange={() => setSel((s) => ({ ...s, dietId: d.id }))} />
                          {sel.dietId === d.id && <HiOutlineCheck />} {d.name}
                        </label>
                      ))}
                    </div>
                    <label className="bk__label" htmlFor="bk-notes">Allergies or anything else the kitchen should know <span>(optional)</span></label>
                    <textarea id="bk-notes" className="bk__textarea" rows={3} maxLength={500} placeholder="e.g. severe nut allergy, no pork" value={sel.dietNotes} onChange={(e) => setSel((s) => ({ ...s, dietNotes: e.target.value }))} />
                  </section>
                )}

                {step === "review" && block && (
                  <section>
                    <h2 className="bk__h2">Check your booking</h2>
                    <p className="bk__lead">Make sure everything is right before you confirm.</p>
                    <dl className="pc bk__check">
                      <div className="bk__check-row"><dt>Retreat</dt><dd><span>{block.name}</span><small>{range(block.start_date, block.end_date)}{block.location ? ` · ${block.location}` : ""}</small></dd>{!lockedDates && <button type="button" onClick={() => go("dates")}>Change</button>}</div>
                      {pathway && <div className="bk__check-row"><dt>Pathway</dt><dd><span>{pathway.name}</span><small>{range(pathway.start_date, pathway.end_date)} · {pathway.nights} nights · {money(pathway.price)}</small></dd><button type="button" onClick={() => go("pathway")}>Change</button></div>}
                      {room && <div className="bk__check-row"><dt>Room</dt><dd><span>{room.name}</span><small>{q.roomTotal > 0 ? (pathway ? `${money(q.nightly)} × ${q.nights} nights = ${money(q.roomTotal)}` : money(q.roomTotal)) : "Included"}</small></dd>{!data.reservedRoomTypeId && <button type="button" onClick={() => go("room")}>Change</button>}</div>}
                      {block.activities.length > 0 && <div className="bk__check-row"><dt>Activity</dt><dd><span>{activity ? activity.name : "Decide later"}</span></dd><button type="button" onClick={() => go("activity")}>Change</button></div>}
                      {data.diets.length > 0 && <div className="bk__check-row"><dt>Diet</dt><dd><span>{diet?.name || "—"}</span>{sel.dietNotes && <small>{sel.dietNotes}</small>}</dd><button type="button" onClick={() => go("diet")}>Change</button></div>}
                      <div className="bk__check-row bk__check-row--total"><dt>Total</dt><dd><span>{money(q.total)}</span><small>No payment is taken today</small></dd></div>
                    </dl>
                    <p className="bk__fine">By confirming, you're reserving your place. Our team will contact you about payment and send your preparation guide. Plans change — if you need to cancel, just let us know.</p>
                  </section>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="bk__nav">
              {idx > 0 && !(idx === 1 && lockedDates) ? (
                <button type="button" className="p-btn p-btn--outline" onClick={back}><HiOutlineArrowLeft /> Back</button>
              ) : <span />}
              <button type="button" className="p-btn p-btn--rust bk__nav-next" disabled={!canContinue || busy} onClick={next}>
                {step === "review" ? (busy ? "Confirming…" : "Confirm booking") : "Continue"} <HiOutlineArrowRight />
              </button>
            </div>
          </div>

          <Summary block={block} pathway={pathway} room={room} activity={activity} diet={diet} q={q}
            onContinue={next} continueLabel={step === "review" ? "Confirm booking" : "Continue"} canContinue={canContinue} busy={busy} />
        </div>

        {/* Mobile bottom bar */}
        <div className="bk__mbar">
          <div className="bk__mbar-total"><small>Total</small><strong>{money(q.total)}</strong></div>
          <button type="button" className="bk__continue" disabled={!canContinue || busy} onClick={next}>
            {step === "review" ? (busy ? "Confirming…" : "Confirm") : "Continue"} <HiOutlineArrowRight />
          </button>
        </div>
      </>
    );
  }

  return (
    <PortalLayout eyebrow="Book" pageTitle={data?.booking ? "Your Booking" : "Book Your Retreat"} quote="The best time to plant a tree was 20 years ago. The second best time is now." quoteAuthor="Proverb">
      {content}
    </PortalLayout>
  );
}
